"use client";

import React from "react";
import { ArrowRight, CircleCheck as CheckCircle2, Cog, CircleQuestionMark as HelpCircle, Wrench } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const pricingFactors = [{
  id: "factor-1",
  num: "01",
  title: "Condition",
  description: "What is known about the individual unit?",
  icon: HelpCircle
}, {
  id: "factor-2",
  num: "02",
  title: "Included Equipment",
  description: "Does the price cover the controller, inflatable, blower and other required components?",
  icon: CheckCircle2
}, {
  id: "factor-3",
  num: "03",
  title: "Known Repairs",
  description: "Has anything important been replaced or repaired?",
  icon: Wrench
}, {
  id: "factor-4",
  num: "04",
  title: "Parts Availability",
  description: "Can compatible components still be sourced?",
  icon: Cog
}];
export default function Usedbullpricing() {
  const prefersReducedMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: prefersReducedMotion ? 0 : 0.05
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: prefersReducedMotion ? 1 : 0,
      y: prefersReducedMotion ? 0 : 12
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: "easeOut"
      }
    }
  };
  return <section id="usedmechanicalbullpricing" data-nav="light" className="border-b border-border bg-background py-16 text-foreground md:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header Block */}
        <div className="max-w-3xl">
          <h2 className="font-serif text-3xl font-bold uppercase tracking-tight sm:text-4xl lg:text-5xl text-foreground">
            Used Mechanical Bull Pricing
          </h2>

          <div className="mt-6 space-y-3 font-sans text-base leading-relaxed text-muted-foreground sm:text-lg">
            <p>
              There is no single useful price for every used mechanical bull. Price depends on the exact machine, its age, condition, included components, previous repairs, controller, inflatable condition, accessories and current availability.
            </p>
            <p>
              For example, two machines of the same model may have very different values if one includes a complete operating package while the other requires additional components.
            </p>
          </div>
        </div>

        {/* 4-Card Responsive Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {pricingFactors.map((factor, index) => {
          const IconComponent = factor.icon;
          return <motion.div key={factor.id} data-index={index} variants={itemVariants} className="group relative flex flex-col justify-between border border-border bg-card p-6 rounded-none shadow-xs transition-[border-color,box-shadow] hover:border-primary/60 hover:shadow-sm">
                <div>
                  <div className="flex items-center justify-between border-b border-border/80 pb-4">
                    <span className="font-mono text-xs font-semibold tracking-wider text-muted-foreground">
                      {factor.num}
                    </span>
                    <IconComponent className="size-5 text-muted-foreground transition-colors group-hover:text-primary" />
                  </div>

                  <h3 className="mt-5 font-serif text-xl font-bold uppercase tracking-tight text-card-foreground">
                    {factor.title}
                  </h3>

                  <p className="mt-3 font-sans text-sm leading-normal text-muted-foreground">
                    {factor.description}
                  </p>
                </div>

                <div className="mt-6 h-0.5 w-8 bg-border transition-[width,background-color] group-hover:w-full group-hover:bg-primary" />
              </motion.div>;
        })}
        </motion.div>

        {/* Enclosed Industrial Callout Block */}
        <motion.div initial={{
        opacity: prefersReducedMotion ? 1 : 0,
        y: prefersReducedMotion ? 0 : 12
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.3
      }} transition={{
        duration: 0.3,
        ease: "easeOut"
      }} className="mt-12 border border-border bg-muted/60 p-6 sm:p-8 rounded-none">
          <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
            <div className="max-w-2xl">
              <p className="text-base font-medium text-foreground sm:text-lg">
                Our Mechanical Bull Price Guide explains the broader factors that affect the cost of new and used equipment.
              </p>
            </div>

            <div className="shrink-0">
              <Button asChild size="lg" className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90 font-mono text-xs tracking-wider uppercase px-6 py-5 shadow-xs">
                <Link to="/MechanicalBullPriceGuidePage">
                  <span>Read The Price Guide</span>
                  <ArrowRight className="size-4 ml-1" />
                </Link>
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}